import React from "react";
import "./Resources.css";

import AccordionDetails, {
  accordionDetailsClasses,
} from "@mui/joy/AccordionDetails";
import AccordionSummary, {
  accordionSummaryClasses,
} from "@mui/joy/AccordionSummary";
import {
  Typography,
  Box,
  Accordion,
  AccordionGroup,
  Stack,
  Button,
  Link,
  List,
  ListItem,
} from "@mui/joy";

const FrequentlyAskedQuestions = () => {
  const goToLegislators = (event) => {
    event.preventDefault();
    window.history.pushState({}, "", "/legislators");

    // communicate to Routes that URL has changed
    const navEvent = new PopStateEvent("popstate");
    window.dispatchEvent(navEvent);
  };

  return (
    <Stack direction={"column"} padding={8} spacing={4}>
      <Stack direction={"column"} spacing={1} sx={{ alignItems: "center" }}>
        <Typography level="h2" style={{ textAlign: "center" }}>
          Take Action
        </Typography>
        <Typography level="body-md" style={{ textAlign: "center" }}>
          Links and other resources to help you take action on a federal and
          state level.
        </Typography>
        <Button
          variant="soft"
          onClick={(e) => goToLegislators(e)}
          sx={{ width: "25%" }}
        >
          Find your representative
        </Button>
      </Stack>
      <AccordionGroup
        variant="outlined"
        color="neutral"
        transition="0.2s ease"
        size="lg"
        sx={{
          backgroundColor: "white",
          borderRadius: "md",
          [`& .${accordionDetailsClasses.content}.${accordionDetailsClasses.expanded}`]:
            {
              paddingBlock: "1rem",
            },
          [`& .${accordionSummaryClasses.button}`]: {
            paddingBlock: "1rem",
          },
        }}
      >
        <Accordion color="neutral">
          <AccordionSummary>
            <Typography
              level="h4"
              sx={{ textAlign: "left", width: "100%" }}
            >
              Find & Contact Your Representative
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box className="history-container" sx={{ padding: 1 }}>
              <Typography level="body-md" sx={{ textAlign: "left" }}>
                Find your representive in a variety of ways.
                <ul>
                  <li>
                    Use the <Link to="/legislators">Legislators</Link> page and
                    reach for your state in the Senators and House
                    Representatives tabs.
                  </li>
                  <li>
                    Look for a bill you want to take action on in the{" "}
                    <Link to="/bills">Bills & Legislation</Link> page, look into
                    its details, and select "Contact your Representative" when
                    you are ready. Our website will find your local district
                    representative and provide you with a customizable email
                    template for that particular issue.{" "}
                  </li>
                  <li>
                    Head to{" "}
                    <Link to="https://www.house.gov/representatives/find-your-representative">
                      House.gov
                    </Link>{" "}
                    to enter your zip and be directed towards your district
                    house representative's website.
                  </li>
                </ul>
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary>
            <Typography
              level="h4"
              sx={{ textAlign: "left", width: "100%" }}
            >
              Petitions
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box className="history-container" sx={{ padding: 1 }}>
              <Typography level="body-md" sx={{ textAlign: "left" }}>
                Petitions are a powerful way to show lawmakers that an issue
                matters to many people. By gathering signatures, you can push
                for action on important topics. While{" "}
                <strong>petitions.whitehouse.gov</strong>—a website created
                during the Obama administration for petitioning the government
                directly—was dissolved in 2021, you can still create impactful
                petitions using other platforms.
                <ul>
                  <li>
                    <b>Start a petition:</b> Use platforms like{" "}
                    <Link to="https://www.change.org/">Change.org</Link> to
                    create your own.{" "}
                  </li>
                  <li>
                    <b>Sign & Share:</b> Adding your name to petitions and
                    spreading the word can increase their impact.
                  </li>
                </ul>
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary>
            <Typography
              level="h4"
              sx={{ textAlign: "left", width: "100%" }}
            >
              Public Comments
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box className="history-container" sx={{ padding: 1 }}>
              <Typography level="body-md" sx={{ textAlign: "left" }}>
                Before passing new rules, government agencies allow the public
                to share opinions through comments. Your input can help shape
                policies.
                <ul>
                  <li>
                    Find Open Comments: Check{" "}
                    <Link to="https://www.regulations.gov/">
                      Regulations.gov
                    </Link>{" "}
                    to see what policies are open for feedback.{" "}
                  </li>
                  <li>
                    Write an Effective Comment: Be clear, specific, and explain
                    how the policy affects you or others.
                  </li>
                  <li>
                    Submit Before the Deadline: Agencies review comments before
                    making final decisions, so timing matters!
                  </li>
                </ul>
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary>
            <Typography
              level="h4"
              sx={{ textAlign: "left", width: "100%" }}
            >
              Support organizations making a difference
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              <Typography level="body-md" sx={{ textAlign: "left" }}>
                Change happens when people come together to support causes they
                believe in. Whether through donations, volunteering, or
                spreading awareness, you can help organizations fighting for
                justice, democracy, and human rights.
              </Typography>
              <Typography level="body-md" sx={{ textAlign: "left" }}>
                <b>Ways to Get Involved:</b>
                <ul>
                  <li>
                    <b>Donate:</b> Even small contributions help nonprofits
                    continue their work.
                  </li>
                  <li>
                    <b>Volunteer:</b> Many organizations rely on volunteers to
                    support their mission.
                  </li>
                  <li>
                    <b>Stay Informed:</b> Follow their updates and share their
                    work with your network.
                  </li>
                </ul>
              </Typography>
              <Typography level="body-md" sx={{ textAlign: "left" }}>
                <b>Organizations to Explore:</b>
                <ul>
                  <li>
                    <Link
                      to="https://www.aclu.org/"
                      target="_blank"
                      rel="noopener"
                    >
                      ACLU
                    </Link>{" "}
                    – Defends civil liberties and constitutional rights.
                  </li>
                  <li>
                    <Link
                      to="https://www.commoncause.org/"
                      target="_blank"
                      rel="noopener"
                    >
                      Common Cause
                    </Link>{" "}
                    – Advocates for fair elections and government
                    accountability.
                  </li>
                  <li>
                    <Link
                      href="https://www.lwv.org/"
                      target="_blank"
                      rel="noopener"
                    >
                      League of Women Voters
                    </Link>{" "}
                    – Works to empower voters and defend democracy.
                  </li>
                  <li>
                    <Link
                      to="https://www.splcenter.org/"
                      target="_blank"
                      rel="noopener"
                    >
                      Southern Poverty Law Center
                    </Link>{" "}
                    – Fights against hate and extremism.
                  </li>
                </ul>
              </Typography>
            </Stack>
          </AccordionDetails>
        </Accordion>
      </AccordionGroup>
    </Stack>
  );
};

export default FrequentlyAskedQuestions;
